import { ChevronDown, ChevronUp, Globe01 } from '@untitled-ui/icons-react';
import { forwardRef, MutableRefObject, useImperativeHandle, useRef } from 'react';

import { useLocale } from '@/hooks/use-locale';
import { cn } from '@/utils/cn';

import { DropdownWithArrow } from '../icons';

interface LocaleSwitcherProps {
  isDropdownVisible?: boolean;
  onLocaleButtonClick?: () => void;
  onLocaleChange?: (locale: string) => void;
}

export interface LocaleSwitcherRef {
  parentDivRef: MutableRefObject<HTMLDivElement>;
  buttonRef: MutableRefObject<HTMLButtonElement>;
  iconUpRef: MutableRefObject<HTMLDivElement>;
  iconDownRef: MutableRefObject<HTMLDivElement>;
}

const LocaleSwitcher = forwardRef<LocaleSwitcherRef, LocaleSwitcherProps>(
  ({ isDropdownVisible, onLocaleButtonClick, onLocaleChange }, ref) => {
    const parentDivRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const iconUpRef = useRef<HTMLDivElement>(null);
    const iconDownRef = useRef<HTMLDivElement>(null);

    const { locales, currentLocale } = useLocale();

    const handleDropdownItemClick = (locale: string) => {
      onLocaleChange?.(locale);
    };

    const options = Object.entries(locales).map(([value, label]) => ({ value, label }));

    useImperativeHandle(ref, () => ({
      parentDivRef,
      buttonRef,
      iconUpRef,
      iconDownRef,
    }));

    return (
      <div ref={parentDivRef} className="inline-block relative text-left">
        <button
          type="button"
          className="inline-flex justify-center items-center bg-white hover:bg-slate-100 p-2 rounded-lg w-full font-medium text-base text-brand-brown"
          id="options-menu"
          aria-haspopup="true"
          aria-expanded="true"
          onClick={onLocaleButtonClick}
          ref={buttonRef}
        >
          <Globe01 className="mr-2 text-xl" />
          <span className="font-[500]">{currentLocale.toUpperCase() || 'Options'}</span>
          {isDropdownVisible ? (
            <span ref={iconUpRef}>
              <ChevronUp />
            </span>
          ) : (
            <span ref={iconDownRef}>
              <ChevronDown />
            </span>
          )}
        </button>

        {isDropdownVisible && (
          <div className="right-0 left-5 absolute mt-2 w-full h-full animate-fade-down animate-duration-300">
            <DropdownWithArrow
              className={cn('absolute drop-shadow-[0_0_10px_rgba(0,_0,_0,_0.2)] rounded-[2rem]', {
                'hidden drop-shadow-none': !isDropdownVisible, // for some reasons on safari shadow is not hidden
              })}
            />
            <div className="top-1 left-[0.4rem] absolute py-4" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
              {options.map((option, index) => (
                <div
                  key={index}
                  onClick={() => handleDropdownItemClick(option.value)}
                  className="block z-10 px-7 py-2 text-gray-7 text-sm hover:text-black cursor-pointer"
                  role="menuitem"
                >
                  {option.value.toUpperCase()}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  },
);

LocaleSwitcher.displayName = 'LocaleSwitcher';

export default LocaleSwitcher;
